import {
  cardCloserOf,
  cardWatcherResolutionOf,
  type DeferredDismissState,
} from '@littlebird/action-cards';
import { cardState } from '@littlebird/action-cards-view';
import { observer } from 'mobx-react-lite';
import { useContext, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { StaffBackendLabel } from '@/components/blocks/staff-backend-label';
import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
} from '@/features/action-cards/analytics';
import { trackEvent } from '@/utils/analytics';
import { openUntrustedUrl } from '@/utils/external-links';

import { CardV2 } from '../CardV2';
import { restoreCard } from '../commands';
import {
  HostIconActionsContext,
  HostTrailingActionsContext,
} from '../registry';

import { FeedCardMoreMenu } from './FeedCardMoreMenu';
import { FeedDensityContext, isDenseFeed } from './feedDensity';
import { FeedFrameHeader } from './FeedFrameHeader';
import { FeedKeysAnsweredContext, FeedKeyTooltip } from './FeedKeyTooltip';
import { FeedMorph } from './FeedMorph';
import {
  FeedSourceChip,
  FeedSourceMenuItem,
  hasFeedSourceLine,
} from './FeedSourceChip';
import { FEED_ROW_META_CLASS } from './feedStyles';
import { HistoryRow } from './HistoryRow';
import { historyRowView } from './historyRowView';

import type { ActionCard } from '@littlebird/sdk';

type HistoryCardProps = {
  card: ActionCard;
  icon: ReactNode;
  body?: ReactNode;
  appName: string;
  // The day the card left the Feed, which line 2 names in place of an age
  day: string;
  open: boolean;
  onToggle: () => void;
  deferredDismiss: DeferredDismissState;
  // Set where the host opens the card's chat in place; without it the card's chat opens in a new tab
  onOpenInCardChat?: (seed?: string) => void;
  onOpenSource?: () => void;
  // Shows the restored card back in the Feed, from the toast's View
  onViewRestored: () => void;
};

// A card in History: its row, opening into the read-only frame; done cards never run again here
export const HistoryCard = observer(function HistoryCard({
  card,
  icon,
  body,
  appName,
  day,
  open,
  onToggle,
  deferredDismiss,
  onOpenInCardChat,
  onOpenSource,
  onViewRestored,
}: HistoryCardProps) {
  // React Compiler would cache `card.row` on the stable MobX card and freeze the row
  'use no memo';
  const [restoring, setRestoring] = useState(false);
  const density = useContext(FeedDensityContext);
  const framed = open;
  const state = cardState(card.row);
  const watcherResolution = cardWatcherResolutionOf(card);
  const view = historyRowView(state, watcherResolution, cardCloserOf(card));
  if (!view) return null;
  const staffLabel = (
    <StaffBackendLabel backend={card.backend} className={FEED_ROW_META_CLASS} />
  );

  const restore = async () => {
    const context = actionCardAnalyticsContext('web_feeds', {
      card_expanded: framed,
    });
    const snapshot = actionCardAnalyticsSnapshot(card, 'web_feeds', {
      card_expanded: framed,
    });
    setRestoring(true);
    try {
      await restoreCard(card, 'web_feeds', context);
      if (watcherResolution)
        trackEvent('CARD_WATCH_RESOLVED_RESTORED', {
          ...snapshot,
          resolution: watcherResolution.kind,
        });
      toast('Restored to Feed', {
        action: { label: 'View', onClick: onViewRestored },
      });
    } catch {
      // The restore command has already told the user with a toast
    } finally {
      setRestoring(false);
    }
  };

  return (
    <FeedMorph open={framed}>
      {framed ? (
        // Dims a dismissed or withdrawn object to 55% (feed.css), as what did not go
        <div data-history-outcome={view.outcome}>
          <FeedKeysAnsweredContext.Provider value={false}>
            <HostIconActionsContext.Provider value={FeedKeyTooltip}>
              <HostTrailingActionsContext.Provider
                value={
                  <FeedSourceChip
                    icon={icon}
                    appName={appName}
                    origin={state.origin}
                    day={day}
                    openUrl={view.openUrl}
                    onOpenUrl={openUntrustedUrl}
                    onOpenSource={onOpenSource}
                  />
                }
              >
                <CardV2
                  card={card}
                  body={body}
                  surface="web_feeds"
                  deferredDismiss={deferredDismiss}
                  reportsShown={false}
                  openCardChat={onOpenInCardChat}
                  header={
                    <FeedFrameHeader
                      view={{ title: view.title, stake: null, failed: false }}
                      // The open card keeps the title its row showed, not a phrase such as "To-do"
                      title={view.title}
                      staffLabel={staffLabel}
                      icon={icon}
                      onClose={onToggle}
                      collapses={isDenseFeed(density)}
                      more={
                        <FeedCardMoreMenu
                          card={card}
                          actionable={false}
                          // "Open in" takes the band, and a host's own body has no band, so where the card came
                          // from stays in ⋯
                          source={
                            (view.openUrl || body !== undefined) &&
                            hasFeedSourceLine(state.origin) ? (
                              <FeedSourceMenuItem
                                icon={icon}
                                origin={state.origin}
                                onOpenSource={onOpenSource}
                              />
                            ) : undefined
                          }
                        />
                      }
                    />
                  }
                />
              </HostTrailingActionsContext.Provider>
            </HostIconActionsContext.Provider>
          </FeedKeysAnsweredContext.Provider>
        </div>
      ) : (
        <HistoryRow
          view={view}
          icon={icon}
          appName={appName}
          staffLabel={staffLabel}
          restoring={restoring}
          onOpen={onToggle}
          onRestore={() => void restore()}
          onOpenUrl={openUntrustedUrl}
        />
      )}
    </FeedMorph>
  );
});
